import { Box, Card, Flex } from '@chakra-ui/react'
import { createFileRoute } from '@tanstack/react-router'
import {
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  Controls,
  type Edge,
  type EdgeChange,
  MarkerType,
  type Node,
  type NodeChange,
  Position,
  ReactFlow,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useCallback, useState } from 'react'
import IncrementCountNode from '~/components/nodes/datahub/IncrementCountNode'
import InputMessageNode from '~/components/nodes/datahub/InputMessageNode'
import OutputMessageNode from '~/components/nodes/datahub/OutputMessageNode'
import SchemaValidationNode from '~/components/nodes/datahub/SchemaValidationNode'
import TransformationNode from '~/components/nodes/datahub/TransformationNode'
import { PageHeader } from '~/components/shell'

export const Route = createFileRoute('/_app/dhftocflow')({
  component: DHFtoCFlowPage,
})

const schema = `
\`\`\`json
{
    "type": "object",
    "properties": {
      "fahrenheit": {
        "type": "number"
      },
      "timestamp": {
        "type": "number"
      }
    },
    "required": [
      "fahrenheit",
      "timestamp"
    ]
  }
`

const script = `
\`\`\`typescript
    function convert(fahrenheit) {
        return Math.floor((fahrenheit - 32) * 5/9);
    }

    function transform(publish, context) {
        publish.payload = {
            "celsius": convert(publish.payload.fahrenheit),
            "timestamp": publish.payload.timestamp
        }

        return publish;
    }
`

const inputMessage = `
\`\`\`json
{
"fahrenheit": 104,
"timestamp": 1790897867
}
`

const outputMessage = `
\`\`\`json
{
"celsius": 40,
"timestamp": 1790897867
}
`

const initNodes: Node[] = [
  // Input
  {
    id: '1',
    data: {
      label: 'InputMessage',
      topic: 'SanFrancisco/Building1/Floor1/Room1/Temperature',
      message: inputMessage,
    },
    type: 'inputMessageNode',
    sourcePosition: Position.Right,
    targetPosition: Position.Right,
    position: { x: 0, y: 100 },
  },

  // DataHub
  {
    id: '2',
    data: { label: 'Schema Validation', schema: schema },
    type: 'schemaValidationNode',
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    position: { x: 500, y: 0 },
  },
  {
    id: '3',
    data: {
      label: 'Log Error',
      logCounter: 'TemperatureSchemaValidationError',
      message: 'Schema validation failed',
    },
    type: 'incrementCountNode',
    sourcePosition: Position.Top,
    targetPosition: Position.Top,
    position: { x: 500, y: 600 },
  },
  {
    id: '4',
    data: { label: 'Transformation', script: script },
    type: 'transformationNode',
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    position: { x: 900, y: 0 },
  },

  // Output
  {
    id: '5',
    data: {
      label: 'OutputMessage',
      topic: 'SanFrancisco/Building1/Floor1/Room1/Temperature',
      message: outputMessage,
    },
    type: 'outputMessageNode',
    targetPosition: Position.Left,
    sourcePosition: Position.Left,
    position: { x: 1600, y: 100 },
  },
]

const initEdges: Edge[] = [
  {
    id: '12',
    source: '1',
    target: '2',
    label: 'De-serialize',
    style: { stroke: '#037DA5', strokeWidth: 5 },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#037DA5' },
  },
  {
    id: '23',
    source: '2',
    sourceHandle: 'b',
    target: '3',
    label: 'Error',
    style: { stroke: '#e91e63', strokeWidth: 5 },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#e91e63' },
  },
  {
    id: '24',
    source: '2',
    sourceHandle: 'a',
    target: '4',
    label: 'Success',
    style: { stroke: '#10b981', strokeWidth: 5 },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#10b981' },
  },
  {
    id: '45',
    source: '4',
    target: '5',
    label: 'Serialize',
    style: { stroke: '#037DA5', strokeWidth: 5 },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#037DA5' },
  },
]

const nodeTypes = {
  inputMessageNode: InputMessageNode,
  schemaValidationNode: SchemaValidationNode,
  incrementCountNode: IncrementCountNode,
  transformationNode: TransformationNode,
  outputMessageNode: OutputMessageNode,
}

function DHFtoCFlowPage() {
  const [nodes, setNodes] = useState<Node[]>(initNodes)
  const [edges, setEdges] = useState<Edge[]>(initEdges)

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  )
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  )

  return (
    <Flex direction="column" h="100%" gap={4}>
      <PageHeader
        title="Fahrenheit to Celsius Conversion Flow"
        subtitle="HiveMQ DataHub pipeline for converting temperature readings from Fahrenheit to Celsius"
      />

      <Card.Root flex={1} minH={0} overflow="hidden">
        <Box h="100%" w="100%">
          <ReactFlow
            nodes={nodes}
            onNodesChange={onNodesChange}
            edges={edges}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            className="flow-customization"
          >
            <Background />
            <Controls />
          </ReactFlow>
        </Box>
      </Card.Root>
    </Flex>
  )
}
